import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Shield, Menu, X } from "lucide-react";
import { useState } from "react";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/daily/theme", label: "Theme" },
  { to: "/daily/promo", label: "Promo" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/stats", label: "Stats" },
  { to: "/how-to-play", label: "How to Play" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-red-900/30">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="relative">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center red-glow">
              <span className="font-display font-black text-white text-sm">P</span>
            </div>
          </div>
          <span className="font-display font-black text-xl tracking-tight">
            <span className="text-white">POP</span>
            <span className="text-red-500">LESS</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-colors ${
                  isActive ? "text-white bg-red-600/20" : "text-slate-400 hover:text-white"
                }`
              }>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Button variant="ghost" size="sm" onClick={() => nav("/admin")}
              className="hidden sm:inline-flex text-slate-300 hover:text-white"
              data-testid="nav-admin-btn">
              <Shield className="h-4 w-4 mr-1" /> Admin
            </Button>
          )}
          {user ? (
            <>
              <span className="hidden sm:inline text-xs text-slate-400 font-mono">{user.name || user.email}</span>
              <Button variant="outline" size="sm" onClick={logout}
                data-testid="nav-logout-btn"
                className="border-slate-700 text-slate-200 hover:bg-red-950/40 hover:text-white">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => nav("/login")} data-testid="nav-login-btn"
              className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider">
              <User className="h-4 w-4 mr-1" /> Sign In
            </Button>
          )}
          <button className="md:hidden text-slate-300 p-1" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-800 bg-black/90 px-4 py-3 space-y-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block py-2 px-3 rounded-lg text-sm font-semibold uppercase tracking-wider ${
                  isActive ? "text-white bg-red-600/20" : "text-slate-400"
                }`
              }>
              {l.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" onClick={() => setOpen(false)}
              className="block py-2 px-3 rounded-lg text-sm font-semibold uppercase text-slate-300">
              Admin
            </NavLink>
          )}
        </div>
      )}
    </header>
  );
}
